const payload = (value) => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch (_) { return {} }
}
const readable = (value) => {
  const labels = { in_queue: 'ASSIGNED', done: 'ASSIGNED', assigned: 'ASSIGNED', replaced: 'REPLACED', unavailable: 'NOT AVAILABLE', inventory_restored: 'Inventory restored', assignee: 'SA', auditor: 'Auditor',
    initial_backfill: 'Initial queue population', appraisal_approved: 'Appraisal approved',
    reappraisal_approved: 'Re-appraisal approved', reprice: 'Price change',
    detail_confirm_uploaded: 'Item detail page', bulk_checkbox: 'Bulk selection',
    manual_owner_reupload: 'Owner re-upload', manual_owner_reassign: 'Owner reassignment', manual_owner_swap_restore: 'Owner swap restore',
    never: 'Never uploaded (Count = 0)', ever: 'Previously uploaded (Count > 0)' }
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'object') return '-'
  return labels[value] || String(value).replace(/_/g, ' ')
}
export const presentAudit = (entry = {}) => {
  const before = payload(entry.before), after = payload(entry.after)
  const data = { ...before, ...after }
  const activity = entry.activity || ''
  const details = []
  const add = (label, value) => { if (value !== undefined && value !== null && value !== '') details.push([label, String(value)]) }
  const change = (label, key) => {
    if (!(key in before) && !(key in after)) return
    add(label, key in before && key in after && before[key] !== after[key]
      ? `${readable(before[key])} → ${readable(after[key])}` : readable(data[key]))
  }
  let title = 'IG Story activity', summary = 'An IG Story activity was recorded.', color = 'secondary'
  if (activity.startsWith('ig_story_manual_swap-sa-')) {
    const skus = (items) => Array.isArray(items) ? items.map((item) => item?.sku).filter(Boolean).join(', ') : ''
    title = 'SA assignments swapped'; summary = `${data.outgoing_count ?? data.outgoing?.length ?? 0} moved to REPLACED; ${data.incoming_count ?? data.incoming?.length ?? 0} moved to ASSIGNED.`; color = 'info'
    add('Moved to REPLACED', skus(data.outgoing))
    add('Restored to ASSIGNED', skus(data.incoming))
    if (data.queue_count_before !== undefined || data.queue_count_after !== undefined) {
      add('Queue', `${data.queue_count_before ?? '-'} → ${data.queue_count_after ?? '-'}`)
    }
  } else if (activity.startsWith('ig_story_done-')) {
    title = 'IG Story upload completed'; summary = 'The item has been confirmed as uploaded.'; color = 'success'
  } else if (activity === 'ig_story_save_tracking') {
    title = 'Upload confirmation saved'; summary = `${data.count ?? data.assignment_ids?.length ?? 0} item(s) confirmed as uploaded.`; color = 'success'
  } else if (activity.startsWith('ig_story_unavailable-assignment-')) {
    title = 'Unavailable product removed'; summary = 'The product left both operational buckets. Its assignment history was preserved.'; color = 'warning'
    add('IQ', data.inventory_stored_quantity)
    add('SQ', data.inventory_sellable_quantity)
  } else if (activity.startsWith('ig_story_inventory_restored-assignment-')) {
    title = 'Available product restored'; summary = 'IQ and SQ are both 1. The product was automatically assigned again.'; color = 'success'
  } else if (activity.startsWith('ig_story_replaced-')) {
    title = 'Queue item replaced'; summary = data.replacement_source === 'owner_manual_status'
      ? 'The owner moved the assignment to REPLACED.' : 'The previous assignment was replaced by a new item.'; color = 'warning'
  } else if (activity.startsWith('ig_story_manual_assignee_changed-')) {
    title = 'SA assignment changed'; summary = 'The owner moved the active assignment to another SA.'; color = 'info'
    change('SA', 'administrator_name')
  } else if (activity.startsWith('ig_story_deleted-')) {
    title = 'Assignment soft-deleted'; summary = 'The owner removed the assignment from active lists; upload history is retained.'; color = 'danger'
  } else if (activity.startsWith('ig_story_assignment_refreshed-')) {
    title = 'Assignment updated'; summary = 'The assignment was updated for the same SA.'; color = 'info'
  } else if (activity.startsWith('ig_story_assigned-')) {
    title = 'Item added to queue'; summary = 'The item was assigned to an SA for an IG Story upload.'; color = 'primary'
  } else if (activity.startsWith('ig_story_export_')) {
    title = 'Report exported'; summary = `${data.row_count ?? 0} ${data.report === 'progress' ? 'SA summary row(s)' : 'assignment(s)'} exported to ${activity.endsWith('xlsx') ? 'Excel (XLSX)' : 'CSV'}.`; color = 'info'
    const filters = payload(data.filters)
    const filterLabels = { report: 'Report', arrival_from: 'New Arrival from (WIB)', arrival_to: 'New Arrival to (WIB)', count_filter: 'Count', upload_history: 'Upload history', status: 'Status', q: 'Search', group_code: 'Group', internal_category: 'Category', assignee_id: 'SA ID', from_date: 'From date', to_date: 'To date' }
    Object.entries(filterLabels).forEach(([key, label]) => { if (filters[key]) add(`Filter ${label}`, readable(filters[key])) })
    if (!details.length) add('Scope', 'All assignments')
  } else if (activity.startsWith('ig_story_member_')) {
    const created = activity.startsWith('ig_story_member_created'), removed = activity.startsWith('ig_story_member_removed')
    title = created ? 'Member added' : removed ? 'Member removed' : 'Member settings updated'
    summary = removed ? 'The member’s IG Story access was removed.' : 'The member’s IG Story settings were saved.'
    add('Member', data.administrator?.name || data.administrator?.email)
    ;[['Active access', 'active'], ['Queue capacity', 'capacity'], ['Category', 'internal_category'], ['Role', 'member_role'], ['Assignment order', 'sort_order']].forEach(([label, key]) => change(label, key))
  } else if (activity === 'ig_story_initial_assignment_backfill') {
    title = 'Initial queue populated'; summary = 'Initial items were distributed to SA queues.'
    ;['C', 'H'].forEach((key) => { if (data[key] !== undefined) add(`Category ${key}`, `${data[key]} item(s)`) })
  }
  add('Item', data.sku)
  add('SA', data.administrator_name || data.administrator_email)
  if (!activity.startsWith('ig_story_member_')) change('Status', 'status')
  add('Replaced by', data.replaced_by_sku)
  if (data.source_event) add('Trigger', readable(data.source_event))
  if (data.completion_source) add('Confirmation method', readable(data.completion_source))
  if (data.assignment_id) add('Assignment', `#${data.assignment_id}`)
  if (Array.isArray(data.assignment_ids) && data.assignment_ids.length) add('Assignment', data.assignment_ids.map((id) => `#${id}`).join(', '))
  return { title, summary, color, details }
}
